/**
 * @file src/pages/invoice-creation-wizard/components/LineItemsSection.jsx
 *
 * What changed from the original:
 *  - Static data (taxRates, exemptionReasons, specificExemptionReasons,
 *    allowanceChargeReasons) moved outside component — not recreated on
 *    every render and not passed as new prop references to every LineItem
 *  - calculateItemTotals wrapped in useCallback and added to useEffect
 *    dependency array — fixes React lint warning and stale closure risk
 *  - useCallback on handleItemChange — stable reference for child props
 *  - Breakdown box IIFE replaced with a clean computed value
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Icon from "components/AppIcon";
import { useGetProducts } from "pages/product-management/useGetProducts";

// ---------------------------------------------------------------------------
// Static data — outside component to avoid recreation on every render
// ---------------------------------------------------------------------------

const TAX_RATES = [
  { value: 0, label: "0% (Exempt)" },
  { value: 5, label: "5% (Reduced)" },
  { value: 15, label: "15% (Standard)" },
];

const EXEMPTION_REASONS = [
  { value: "", label: "Select reason" },
  { value: "zero_rated", label: "Zero-rated supply" },
  { value: "exempt_supply", label: "Exempt supply" },
  { value: "out_of_scope", label: "Out of scope" },
];

const SPECIFIC_EXEMPTION_REASONS = {
  zero_rated: [
    { value: "", label: "Select specific reason" },
    { value: "VATEX-SA-32", label: "Export of goods" },
    { value: "VATEX-SA-33", label: "Export of services" },
    { value: "VATEX-SA-34-1", label: "The international transport of Goods" },
    { value: "VATEX-SA-34-2", label: "International transport of passengers" },
    {
      value: "VATEX-SA-34-3",
      label:
        "Services directly connected and incidental to a Supply of international passenger transport",
    },
    {
      value: "VATEX-SA-34-4",
      label: "Supply of a qualifying means of transport",
    },
    {
      value: "VATEX-SA-34-5",
      label:
        "Any services relating to Goods or passenger transportation, as defined by the implementing regulations",
    },
    { value: "VATEX-SA-35", label: "Medicines and medical equipment" },
    { value: "VATEX-SA-36", label: "Qualifying metals" },
    { value: "VATEX-SA-EDU", label: "Private education to citizen" },
    { value: "VATEX-SA-HEA", label: "Private healthcare to citizen" },
    { value: "VATEX-SA-MLTRY", label: "Supply of qualified military goods" },
  ],
  exempt_supply: [
    { value: "", label: "Select specific reason" },
    {
      value: "VATEX-SA-29",
      label:
        "Financial services mentioned in Article 29 of the VAT Regulations",
    },
    {
      value: "VATEX-SA-30",
      label:
        "Real estate transactions mentioned in Article 30 of the VAT Regulations",
    },
    {
      value: "VATEX-SA-29-7",
      label:
        "Life insurance services mentioned in Article 29 of the VAT Regulations",
    },
  ],
  out_of_scope: [
    { value: "", label: "Select specific reason" },
    { value: "VATEX-SA-OOS", label: "Out of scope of VAT" },
  ],
};

const ALLOWANCE_CHARGE_REASONS = [
  { value: "", label: "Select reason" },
  { value: "95", label: "Discount" },
  { value: "100", label: "Special agreement" },
  { value: "ADR", label: "Advertising" },
  { value: "DI", label: "Damage insurance" },
  { value: "FC", label: "Freight charge" },
  { value: "IN", label: "Insurance" },
  { value: "PC", label: "Packing costs" },
  { value: "SH", label: "Shipping and handling" },
];

const SELECT_CLS =
  "w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-foreground dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent";

// ---------------------------------------------------------------------------
// Allowance/charge effect calculator — pure, shared by both components
// ---------------------------------------------------------------------------

function calcAllowanceChargeEffect(item) {
  const base = parseFloat(item.allowanceChargeBaseAmount) || 0;
  const multiplier = parseFloat(item.allowanceChargeMultiplier) || 0;
  const amount = parseFloat(item.allowanceChargeAmount) || 0;

  if (item.allowanceChargeIndicator === "false") {
    return base && multiplier ? -(base * (multiplier / 100)) : -amount;
  }

  if (item.allowanceChargeIndicator === "true") {
    return base && multiplier ? base * (multiplier / 100) : amount;
  }

  return 0;
}

// ---------------------------------------------------------------------------
// LineItemsSection
// ---------------------------------------------------------------------------

const getProductSearchText = (product) =>
  [
    product?.name,
    product?.description,
    product?.uniqueCode,
    product?.customsCode,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const getProductSuggestions = (products, query) => {
  const term = String(query || "")
    .trim()
    .toLowerCase();

  if (!term || term.length < 2) return [];

  return products
    .filter((product) => getProductSearchText(product).includes(term))
    .slice(0, 8);
};

const LineItemsSection = ({
  items,
  onItemChange,
  onAddItem,
  onRemoveItem,
  currency,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [initializedItems, setInitializedItems] = useState(new Set());
  const { data: products = [] } = useGetProducts();

  // ── Calculate and push totals back to parent ────────────────────────────
  const calculateItemTotals = useCallback(
    (item, itemId) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      const taxRate = parseFloat(item.taxRate) || 0;

      const subtotal = quantity * unitPrice;
      const allowanceChargeEffect = calcAllowanceChargeEffect(item);
      const adjustedAmount = subtotal + allowanceChargeEffect;
      const taxAmount = item.taxExempt ? 0 : adjustedAmount * (taxRate / 100);
      const total = adjustedAmount + taxAmount;

      onItemChange(itemId, "adjustedAmount", adjustedAmount);
      onItemChange(itemId, "taxAmount", taxAmount);
      onItemChange(itemId, "total", total);
    },
    [onItemChange],
  );

  // ── Trigger calculation for draft-loaded items ──────────────────────────
  useEffect(() => {
    items.forEach((item) => {
      const hasValues =
        (parseFloat(item.quantity) || 0) > 0 &&
        (parseFloat(item.unitPrice) || 0) > 0;
      const notCalculated =
        (!item.taxAmount || item.taxAmount === 0) &&
        (!item.total || item.total === 0);
      const notInitialized = !initializedItems.has(item.id);

      if (hasValues && notCalculated && notInitialized) {
        calculateItemTotals(item, item.id);
        setInitializedItems((prev) => new Set([...prev, item.id]));
      }
    });
  }, [items, calculateItemTotals, initializedItems]);

  // ── Field change with auto-recalculate ──────────────────────────────────
  const handleItemChange = useCallback(
    (itemId, field, value) => {
      onItemChange(itemId, field, value);

      const CALC_FIELDS = new Set([
        "quantity",
        "unitPrice",
        "taxRate",
        "taxExempt",
        "allowanceChargeIndicator",
        "allowanceChargeAmount",
        "allowanceChargeBaseAmount",
        "allowanceChargeMultiplier",
      ]);

      if (CALC_FIELDS.has(field)) {
        const item = items.find((i) => i.id === itemId);
        if (item) calculateItemTotals({ ...item, [field]: value }, itemId);
      }
    },
    [items, onItemChange, calculateItemTotals],
  );

  const handleProductSelect = useCallback(
    (itemId, product) => {
      const currentItem = items.find((item) => item.id === itemId);

      if (!currentItem || !product) return;

      const taxRate = Number(product.taxRate ?? 15);
      const isZeroVat = taxRate === 0;

      const vatCategoryCode = isZeroVat ? product.vatCategoryCode || "" : "S";

      const nextItem = {
        ...currentItem,
        productId: product.id || product._id || "",
        productUniqueCode: product.uniqueCode || "",
        productCustomsCode: product.customsCode || "",
        description: product.name || product.description || "",
        unitPrice: Number(product.unitSellingPrice || 0),
        taxRate,
        taxExempt: isZeroVat,
        vatCategoryCode,

        exemptionCategory: isZeroVat ? product.exemptionCategory || "" : "",

        exemptionReasonCode: isZeroVat ? product.exemptionReasonCode || "" : "",

        exemptionReason: isZeroVat ? product.exemptionReason || "" : "",
      };

      onItemChange(itemId, "productId", nextItem.productId);
      onItemChange(itemId, "productUniqueCode", nextItem.productUniqueCode);
      onItemChange(itemId, "productCustomsCode", nextItem.productCustomsCode);
      onItemChange(itemId, "description", nextItem.description);
      onItemChange(itemId, "unitPrice", nextItem.unitPrice);
      onItemChange(itemId, "taxRate", nextItem.taxRate);
      onItemChange(itemId, "taxExempt", nextItem.taxExempt);
      onItemChange(itemId, "vatCategoryCode", nextItem.vatCategoryCode);
      onItemChange(itemId, "exemptionCategory", nextItem.exemptionCategory);
      onItemChange(itemId, "exemptionReasonCode", nextItem.exemptionReasonCode);
      onItemChange(itemId, "exemptionReason", nextItem.exemptionReason);

      calculateItemTotals(nextItem, itemId);
    },
    [items, onItemChange, calculateItemTotals],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
              <Icon name="Calculator" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                Line Items
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                Add products and services to this invoice
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              iconName="Plus"
              onClick={onAddItem}
            >
              Add Item
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              onClick={() => setIsExpanded(!isExpanded)}
            />
          </div>
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <AnimatePresence>
              {items.map((item, index) => (
                <LineItem
                  key={item.id}
                  item={item}
                  index={index}
                  currency={currency}
                  products={products}
                  onProductSelect={handleProductSelect}
                  onItemChange={handleItemChange}
                  onRemoveItem={onRemoveItem}
                  canRemove={items.length > 1}
                />
              ))}
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center pt-4"
            >
              <Button
                variant="outline"
                size="md"
                iconName="Plus"
                onClick={onAddItem}
                className="border-dashed border-2 hover:border-primary hover:bg-primary-50 dark:hover:bg-primary-900/20"
              >
                Add Another Item
              </Button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// LineItem
// ---------------------------------------------------------------------------

const LineItem = ({
  item,
  index,
  currency,
  products = [],
  onProductSelect,
  onItemChange,
  onRemoveItem,
  canRemove,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);

  const productSuggestions = useMemo(
    () => getProductSuggestions(products, item.description),
    [products, item.description],
  );

  // Breakdown values
  const subtotal =
    (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0);
  const allowanceChargeEffect = calcAllowanceChargeEffect(item);
  const effectDisplay = Math.abs(allowanceChargeEffect).toFixed(2);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600"
    >
      {/* Line item header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
            <span className="text-sm font-medium text-primary">
              {index + 1}
            </span>
          </div>
          <h4 className="font-medium text-foreground dark:text-white">
            Line Item {index + 1}
          </h4>
          {item.vatCategoryCode && (
            <span className="px-2 py-1 text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary rounded">
              VAT: {item.vatCategoryCode}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
          {canRemove && (
            <Button
              variant="ghost"
              size="sm"
              iconName="Trash2"
              onClick={() => onRemoveItem(item.id)}
              className="text-error hover:text-error hover:bg-error-50 dark:hover:bg-error-900/20"
            />
          )}
        </div>
      </div>

      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-4"
        >
          {/* Product / Description */}
          <div className="relative">
            <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
              Product / Description *
            </label>

            <Input
              type="text"
              value={item.description}
              onFocus={() => setIsProductMenuOpen(true)}
              onChange={(e) => {
                onItemChange(item.id, "description", e.target.value);
                setIsProductMenuOpen(true);
              }}
              placeholder="Type product name, SKU, customs code, or description"
            />

            {item.productUniqueCode && (
              <p className="text-xs text-muted-foreground mt-1">
                SKU: {item.productUniqueCode}
                {item.productCustomsCode
                  ? ` · Customs: ${item.productCustomsCode}`
                  : ""}
              </p>
            )}

            {isProductMenuOpen && productSuggestions.length > 0 && (
              <div className="absolute z-30 mt-1 w-full rounded-lg border border-border bg-white dark:bg-gray-800 shadow-lg max-h-64 overflow-y-auto">
                {productSuggestions.map((product) => (
                  <button
                    key={product.id || product._id}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      onProductSelect(item.id, product);
                      setIsProductMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-foreground truncate">
                          {product.name || "Unnamed product"}
                        </div>

                        <div className="text-xs text-muted-foreground truncate">
                          {product.description ||
                            product.uniqueCode ||
                            "No description"}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-foreground">
                          SR {Number(product.unitSellingPrice || 0).toFixed(2)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          VAT {Number(product.taxRate || 0)}%
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Quantity, Unit Price, Subtotal */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Quantity *
              </label>
              <Input
                type="number"
                value={item.quantity}
                onChange={(e) =>
                  onItemChange(
                    item.id,
                    "quantity",
                    parseFloat(e.target.value) || 0,
                  )
                }
                placeholder="0"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Unit Price ({currency}) *
              </label>
              <Input
                type="number"
                value={item.unitPrice}
                onChange={(e) =>
                  onItemChange(
                    item.id,
                    "unitPrice",
                    parseFloat(e.target.value) || 0,
                  )
                }
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Subtotal ({currency})
              </label>
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-foreground dark:text-white">
                {subtotal.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Tax Rate + Exemption Category */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Tax Rate
              </label>
              <select
                value={item.taxRate}
                onChange={(e) => {
                  const rate = parseFloat(e.target.value);
                  onItemChange(item.id, "taxRate", rate);
                  onItemChange(item.id, "taxExempt", rate === 0);

                  if (rate === 15 || rate === 5) {
                    onItemChange(item.id, "vatCategoryCode", "S");
                  }

                  if (rate !== 0) {
                    onItemChange(item.id, "exemptionReason", "");
                    onItemChange(item.id, "exemptionReasonCode", "");
                    onItemChange(item.id, "exemptionCategory", "");
                  } else {
                    onItemChange(item.id, "vatCategoryCode", "");
                  }
                }}
                className={SELECT_CLS}
              >
                {TAX_RATES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            {item.taxExempt && (
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Exemption Category
                </label>
                <select
                  value={item.exemptionCategory || ""}
                  onChange={(e) => {
                    const cat = e.target.value;
                    const catLabel =
                      EXEMPTION_REASONS.find((r) => r.value === cat)?.label ||
                      "";

                    onItemChange(item.id, "exemptionCategory", cat);
                    onItemChange(item.id, "exemptionReason", catLabel);
                    onItemChange(item.id, "exemptionReasonCode", "");

                    const catCode =
                      cat === "zero_rated"
                        ? "Z"
                        : cat === "exempt_supply"
                          ? "E"
                          : cat === "out_of_scope"
                            ? "O"
                            : "";

                    onItemChange(item.id, "vatCategoryCode", catCode);
                  }}
                  className={SELECT_CLS}
                >
                  {EXEMPTION_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Specific Exemption Reason */}
          {item.taxExempt &&
            item.exemptionCategory &&
            SPECIFIC_EXEMPTION_REASONS[item.exemptionCategory] && (
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Specific Exemption Reason
                </label>
                <select
                  value={item.exemptionReasonCode || ""}
                  onChange={(e) => {
                    const code = e.target.value;
                    const reason = SPECIFIC_EXEMPTION_REASONS[
                      item.exemptionCategory
                    ].find((r) => r.value === code);

                    onItemChange(item.id, "exemptionReasonCode", code);

                    if (reason && code) {
                      onItemChange(item.id, "exemptionReason", reason.label);
                    }
                  }}
                  className={SELECT_CLS}
                >
                  {SPECIFIC_EXEMPTION_REASONS[item.exemptionCategory].map(
                    (r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ),
                  )}
                </select>
              </div>
            )}

          {/* Line Allowance/Charge */}
          <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
            <h5 className="font-medium text-foreground dark:text-white mb-4">
              Line Allowance/Charge
            </h5>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                  Type
                </label>
                <select
                  value={item.allowanceChargeIndicator || ""}
                  onChange={(e) => {
                    onItemChange(
                      item.id,
                      "allowanceChargeIndicator",
                      e.target.value,
                    );
                    onItemChange(item.id, "allowanceChargeAmount", 0);
                    onItemChange(item.id, "allowanceChargeBaseAmount", "");
                    onItemChange(item.id, "allowanceChargeMultiplier", "");
                    onItemChange(item.id, "allowanceChargeReasonCode", "");
                    onItemChange(item.id, "allowanceChargeReason", "");
                  }}
                  className={SELECT_CLS}
                >
                  <option value="">No allowance/charge</option>
                  <option value="false">Allowance (Discount)</option>
                  <option value="true">Charge (Additional Fee)</option>
                </select>
              </div>

              {item.allowanceChargeIndicator && (
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Reason
                  </label>
                  <select
                    value={item.allowanceChargeReasonCode || ""}
                    onChange={(e) => {
                      const code = e.target.value;
                      const reason = ALLOWANCE_CHARGE_REASONS.find(
                        (r) => r.value === code,
                      );

                      onItemChange(item.id, "allowanceChargeReasonCode", code);
                      onItemChange(
                        item.id,
                        "allowanceChargeReason",
                        reason?.label ?? "",
                      );
                    }}
                    className={SELECT_CLS}
                  >
                    {ALLOWANCE_CHARGE_REASONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {item.allowanceChargeIndicator && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Amount ({currency})
                    </label>
                    <Input
                      type="number"
                      value={item.allowanceChargeAmount || ""}
                      onChange={(e) =>
                        onItemChange(
                          item.id,
                          "allowanceChargeAmount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Base Amount ({currency})
                    </label>
                    <Input
                      type="number"
                      value={item.allowanceChargeBaseAmount || ""}
                      onChange={(e) =>
                        onItemChange(
                          item.id,
                          "allowanceChargeBaseAmount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      Percentage (%)
                    </label>
                    <Input
                      type="number"
                      value={item.allowanceChargeMultiplier || ""}
                      onChange={(e) =>
                        onItemChange(
                          item.id,
                          "allowanceChargeMultiplier",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-xs text-muted-foreground dark:text-gray-400 mt-1">
                      If base amount is provided, percentage calculates the
                      amount
                    </p>
                  </div>
                </div>

                {/* Breakdown box */}
                <div className="mt-4 p-3 rounded-lg border border-dashed border-gray-300 dark:border-gray-500">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>
                      {subtotal.toFixed(2)} {currency}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm mt-1">
                    <span
                      className={
                        item.allowanceChargeIndicator === "false"
                          ? "text-green-600"
                          : "text-orange-500"
                      }
                    >
                      {item.allowanceChargeIndicator === "false"
                        ? "Allowance (−)"
                        : "Charge (+)"}
                    </span>
                    <span>
                      {item.allowanceChargeIndicator === "false" ? "−" : "+"}
                      {effectDisplay} {currency}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-gray-200">
                    <span>
                      {item.allowanceChargeIndicator === "false"
                        ? "After Allowance"
                        : "After Charge"}
                    </span>
                    <span className="text-primary">
                      {item.adjustedAmount?.toFixed(2) ??
                        (subtotal + allowanceChargeEffect).toFixed(2)}{" "}
                      {currency}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Tax Amount and Total */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Tax Amount ({currency})
              </label>
              <div className="px-3 py-2 bg-gray-100 dark:bg-gray-600 rounded-lg text-foreground dark:text-white">
                {item.taxAmount?.toFixed(2) ?? "0.00"}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                Line Total ({currency})
              </label>
              <div className="px-3 py-2 bg-primary-100 dark:bg-primary-900/20 rounded-lg text-primary font-medium">
                {item.total?.toFixed(2) ?? "0.00"}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default LineItemsSection;
